import { AnimatePresence, motion, type PanInfo } from 'framer-motion';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { cn } from '../lib/cn';
import { springGentle } from '../tokens';

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

type BottomSheetProps = {
  open: boolean;
  onClose: () => void;
  title?: string;
  children: ReactNode;
  /** Disable drag-to-dismiss (e.g. for forms with required input). */
  dismissible?: boolean;
  className?: string;
};

const DISMISS_DRAG_PX = 120;
const DISMISS_DRAG_VELOCITY = 600;

/**
 * Slide-up modal sheet with scrim, drag-to-dismiss, Escape to close,
 * and body scroll lock. Top corners are rounded; bottom flush with the
 * viewport edge.
 */
export function BottomSheet({
  open,
  onClose,
  title,
  children,
  dismissible = true,
  className,
}: BottomSheetProps) {
  const sheetRef = useRef<HTMLDivElement | null>(null);
  const previouslyFocused = useRef<HTMLElement | null>(null);
  // Pixels the on-screen keyboard occludes at the bottom of the layout
  // viewport. Pads the sheet wrapper so the sheet (which is items-end)
  // floats above the keyboard instead of disappearing under it.
  const [keyboardInset, setKeyboardInset] = useState(0);

  // Lock body scroll while open and bind Escape to close.
  useEffect(() => {
    if (!open) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && dismissible) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener('keydown', onKey);
    };
  }, [open, dismissible, onClose]);

  // Track the on-screen keyboard via visualViewport. When the keyboard
  // opens the visual viewport shrinks while the layout viewport stays
  // put — the diff is the inset we need to push the sheet up by. Also
  // scrolls the focused field back into view once the inset settles, in
  // case the browser's auto-scroll lost it during the resize.
  useEffect(() => {
    if (!open) return;
    const vv =
      typeof window !== 'undefined' ? window.visualViewport : undefined;
    if (!vv) return;
    let raf = 0;
    const update = () => {
      const inset = Math.max(
        0,
        window.innerHeight - vv.height - vv.offsetTop,
      );
      setKeyboardInset(inset);
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const active = document.activeElement as HTMLElement | null;
        if (
          inset > 0 &&
          active &&
          sheetRef.current?.contains(active) &&
          typeof active.scrollIntoView === 'function'
        ) {
          active.scrollIntoView({ block: 'center', behavior: 'smooth' });
        }
      });
    };
    update();
    vv.addEventListener('resize', update);
    vv.addEventListener('scroll', update);
    return () => {
      vv.removeEventListener('resize', update);
      vv.removeEventListener('scroll', update);
      cancelAnimationFrame(raf);
      setKeyboardInset(0);
    };
  }, [open]);

  // Focus management — capture the previously focused element on open,
  // move focus to the first interactive element inside the sheet (or the
  // sheet itself), and restore focus on close. Doesn't trap focus
  // (Tab cycling out is fine for our scope), just handles the entry +
  // exit so screen readers and keyboard users land somewhere sensible.
  useEffect(() => {
    if (!open) return;
    if (typeof document !== 'undefined') {
      previouslyFocused.current = document.activeElement as HTMLElement | null;
    }
    // Defer one frame so the sheet has mounted.
    const id = window.setTimeout(() => {
      const el = sheetRef.current;
      if (!el) return;
      const first = el.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);
      // Skip text inputs to avoid auto-popping the system keyboard on
      // sheets that contain a NumPad (the NumPad itself is the input).
      const target =
        first && first.tagName === 'INPUT' && (first as HTMLInputElement).type === 'text'
          ? first
          : first ?? el;
      target.focus({ preventScroll: true });
    }, 50);
    return () => {
      window.clearTimeout(id);
      const prev = previouslyFocused.current;
      if (prev && typeof prev.focus === 'function') {
        prev.focus({ preventScroll: true });
      }
    };
  }, [open]);

  const handleDragEnd = (_: unknown, info: PanInfo) => {
    if (!dismissible) return;
    if (info.offset.y > DISMISS_DRAG_PX || info.velocity.y > DISMISS_DRAG_VELOCITY) {
      onClose();
    }
  };

  return (
    <AnimatePresence>
      {open ? (
        <div
          className="fixed inset-0 z-50 flex items-end justify-center"
          style={{ paddingBottom: keyboardInset }}
        >
          <motion.div
            key="scrim"
            className="absolute inset-0 scrim"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={dismissible ? onClose : undefined}
            aria-hidden
          />
          <motion.div
            key="sheet"
            ref={sheetRef}
            role="dialog"
            aria-modal="true"
            aria-label={title}
            tabIndex={-1}
            className={cn(
              'relative w-full max-w-content bg-surface rounded-t-sheet shadow-lg',
              'flex flex-col max-h-[90vh] overflow-hidden',
              'pb-[env(safe-area-inset-bottom)] outline-none',
              className,
            )}
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={springGentle}
            drag={dismissible ? 'y' : false}
            dragConstraints={{ top: 0, bottom: 0 }}
            dragElastic={{ top: 0, bottom: 0.4 }}
            onDragEnd={handleDragEnd}
          >
            {/* Drag handle */}
            <div className="pt-3 pb-1 flex items-center justify-center shrink-0">
              <div className="w-9 h-[5px] rounded-pill bg-text-tertiary/40" />
            </div>
            {title ? (
              <div className="px-screen-x pb-3 shrink-0">
                <h2 className="text-section text-text-primary text-center">
                  {title}
                </h2>
              </div>
            ) : null}
            <div className="flex-1 overflow-y-auto overscroll-contain">
              {children}
            </div>
          </motion.div>
        </div>
      ) : null}
    </AnimatePresence>
  );
}
